'use client'

import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { HelpCircle } from "lucide-react"
import { cn } from "@/lib/utils"

interface InfoTooltipProps {
  text: string
  className?: string
}

const POPOVER_WIDTH = 256
const VIEWPORT_MARGIN = 12

/** Маленький «?» с пояснением по клику — в отличие от hover-тултипа работает и на тач-устройствах. */
export function InfoTooltip({ text, className }: InfoTooltipProps) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (!btnRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const toggle = () => {
    if (!open) {
      const iconRect = btnRef.current?.getBoundingClientRect()
      if (iconRect) {
        const left = Math.min(
          Math.max(iconRect.right - POPOVER_WIDTH, VIEWPORT_MARGIN),
          window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN
        )
        setCoords({ top: iconRect.bottom + 8, left })
      }
    }
    setOpen((v) => !v)
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className={cn("text-gray-500 hover:text-gray-300 transition-colors", className)}
        aria-label="Пояснение"
        aria-expanded={open}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && coords && typeof document !== 'undefined' && createPortal(
        <div
          ref={popoverRef}
          role="tooltip"
          style={{ top: coords.top, left: coords.left, width: POPOVER_WIDTH }}
          className="fixed z-50 rounded-lg border border-gray-700 bg-gray-900 p-3 text-xs leading-relaxed text-gray-300 shadow-xl animate-in fade-in-0 zoom-in-95 duration-150"
        >
          {text}
        </div>,
        document.body
      )}
    </>
  )
}
